/**
 * Root Vuex store configuration for the KeyReply Dashboard.
 *
 * @module store
 * @description Central state management store that orchestrates all application state through
 * modular architecture. Combines root state, actions, mutations, and getters with specialized
 * feature modules for livechat, editors, integrations, and AI capabilities.
 *
 * @example
 * // Access root state
 * this.$store.state.account
 *
 * // Access module state
 * this.$store.state.livechat.chats
 *
 * // Dispatch root action
 * this.$store.dispatch('LOAD_BOTS')
 *
 * // Dispatch module action
 * this.$store.dispatch('livechat/LOAD_CHATS', { status: 'active' })
 */
import Vue from "vue";
import Vuex, { Store } from "vuex";
import state from "@/store/initialState";
import actions from "@/store/actions";
import mutations from "@/store/mutations";
import { RootState } from "./types";
import livechat from "@/store/modules/livechat";
import supervisor from "@/store/modules/supervisor";
import floweditor from "@/store/modules/floweditor";
import apieditor from "@/store/modules/apieditor";
import whatsapp from "@/store/modules/whatsapp";
import watson from "@/store/modules/watson";
import faq from "@/store/modules/faq";
import accesscontrol from "@/store/modules/accesscontrol";
import department from "@/store/modules/department";
import prism from "@/store/modules/prism";
import outreach from "@/store/modules/outreach";
// @ts-ignore
import makerChecker from "./modules/makerchecker";
import editor from "./modules/editor";
import broadcast from "./modules/broadcast";
import fileUpload from "@/store/modules/filestorage";
import zoom from "./modules/zoom";
import ecare from "./modules/ecare";
import customercare from "./modules/customercare";
import genai from "./modules/genai";
import smart_classifier from "./modules/smartclassifier";

import {
  changes,
  contentNodeNames,
  getAgentName,
  getAgentSOEId,
  intentTestingCompleted,
  isRtlModeEnabled,
  isShowAdvanced,
  isUiBetaEnabled,
  releaseId,
} from "./getters";

Vue.use(Vuex);

const store: Store<RootState> = new Vuex.Store({
  modules: {
    livechat,
    supervisor,
    floweditor,
    apieditor,
    whatsapp,
    faq,
    accesscontrol,
    department,
    watson,
    makerChecker,
    editor,
    broadcast,
    prism,
    fileUpload,
    zoom,
    outreach,
    ecare,
    customercare,
    genai,
    smart_classifier,
  },
  // @ts-ignore
  state,
  actions,
  mutations,
  getters: {
    changes,
    contentNodeNames,
    getAgentName,
    getAgentSOEId,
    intentTestingCompleted,
    isRtlModeEnabled,
    isShowAdvanced,
    isUiBetaEnabled,
    releaseId,
  },
});

export default store;
